/**
 * Loading/error rules shared by the Events screen and its two Redux slices (issue #440).
 *
 * The Events page could spin forever with a blank body and no message: the Algolia
 * bootstrap chain (token -> key -> index) swallowed its rejections into a slice with no
 * `error` field, while `event_reservations.loading` — which initialises `true` — is only
 * ever cleared by a search that, without an index, is never dispatched.
 *
 * Kept dependency-free so it can be unit tested with `node --test`.
 */

/** Shown when a search fails without a usable message of its own. */
export const DEFAULT_SEARCH_ERROR = "We couldn't load your events. Please try again.";

/** Per-stage copy for the Algolia bootstrap chain, keyed by thunk stage. */
export const ALGOLIA_STAGE_ERRORS = {
    token: "We couldn't verify your session for search. Please try again.",
    key: "We couldn't load your search credentials. Please try again.",
    index: "We couldn't open the events search index. Please try again.",
};

/**
 * Best available message from a rejected RTK action.
 *
 * @param {object} action Rejected action.
 * @param {string} fallback Used when the action carries nothing readable.
 * @returns {string}
 */
export const resolveErrorMessage = (action, fallback) => {
    const payload = action && action.payload;
    if (typeof payload === "string" && payload.trim()) return payload;
    const message = action && action.error && action.error.message;
    if (typeof message === "string" && message.trim()) return message;
    return fallback;
};

/**
 * Whether a rejected action is a `condition` abort rather than a real failure.
 *
 * RTK dispatches `rejected` with `meta.condition` when a thunk's `condition` returns
 * false. The request never ran, so it must not touch loading or error state.
 *
 * @param {object} action Rejected action.
 * @returns {boolean}
 */
export const isConditionAbort = (action) => Boolean(action && action.meta && action.meta.condition);

/**
 * Whether the reservations index is bootstrapped enough to search.
 *
 * Guards the thunk so it cannot call `.search` on a null index — that threw a raw
 * TypeError which `rejectWithValue` then pinned into user-visible error state.
 *
 * @param {object|null|undefined} eventReservations `event_reservations` slice state.
 * @returns {boolean}
 */
export const canRunReservationSearch = (eventReservations) =>
    typeof (eventReservations && eventReservations.index && eventReservations.index.search) === "function";

/**
 * Reducer body for `getReservationsFromAlgolia.pending`.
 *
 * Clears the previous error so a stale message cannot outlive a retry.
 */
export const applySearchPending = (state) => {
    state.loading = true;
    state.error = null;
    return state;
};

/**
 * Reducer body for `getReservationsFromAlgolia.rejected`.
 *
 * Also clears `filtering`, which was only ever reset on success — a rejected search left
 * it latched true, which permanently disabled the filter and sort effects on the page.
 */
export const applySearchRejection = (state, action) => {
    if (isConditionAbort(action)) return state;
    state.error = resolveErrorMessage(action, DEFAULT_SEARCH_ERROR);
    state.loading = false;
    state.filtering = false;
    return state;
};

/** Reducer body for any `setAlgolia*.pending`. */
export const applyAlgoliaPending = (state) => {
    state.loading = true;
    state.error = null;
    return state;
};

/**
 * Reducer body for any `setAlgolia*.rejected`.
 *
 * Clearing `loading` matters as much as recording the error: the bootstrap slice used to
 * stay `loading: true` forever after a rejection, so nothing downstream could tell
 * "still starting up" from "broken".
 *
 * @param {object} state Algolia slice state.
 * @param {object} action Rejected action.
 * @param {'token'|'key'|'index'} stage Which bootstrap step failed.
 * @param {string} field Slice field to null out.
 */
export const applyAlgoliaRejection = (state, action, stage, field) => {
    state[field] = null;
    state.loading = false;
    state.error = resolveErrorMessage(action, ALGOLIA_STAGE_ERRORS[stage] || DEFAULT_SEARCH_ERROR);
    return state;
};

/**
 * Normalises a calendar deep-link `eventDay` param to `YYYY-MM-DD`.
 *
 * Accepts `YYYYMMDD` (what WidgetCalendar emits) and `YYYY-MM-DD`. Returns null for
 * anything else, including calendar-invalid dates like `20260231` that a lenient parser
 * would silently roll forward into March.
 *
 * @param {string|null|undefined} raw Raw query-string value.
 * @returns {string|null} ISO day, or null when unusable.
 */
export const parseEventDayParam = (raw) => {
    if (typeof raw !== "string") return null;
    const trimmed = raw.trim();
    let iso = null;
    if (/^\d{8}$/.test(trimmed)) {
        iso = `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`;
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        iso = trimmed;
    }
    if (!iso) return null;
    const [year, month, day] = iso.split("-").map(Number);
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
        return null;
    }
    return iso;
};

/**
 * Whether search is unusable, so results cannot be trusted.
 *
 * @param {{algoliaError: ?string, error: ?string}} status
 * @returns {boolean}
 */
export const isSearchUnavailable = ({algoliaError, error}) => Boolean(algoliaError || error);

/**
 * Whether the Events screen should show its loading spinner.
 *
 * A bootstrap failure leaves `event_reservations.loading` at its initial `true` forever,
 * because the search that would clear it never runs. Treating that as "done, but broken"
 * is what stops the spinner from latching — the error alert explains the rest.
 *
 * @param {{loading: boolean, algoliaError: ?string}} status
 * @returns {boolean}
 */
export const isSearchBusy = ({loading, algoliaError}) => Boolean(loading) && !algoliaError;
